import DownArrow from 'icons/down.svg';
import UpArrow from 'icons/up.svg';
import { Button } from 'ui/button/button.module';
import { Calendar } from 'ui/calendar/calendar.module';
import FormInput from 'ui/FormInput/formInput.module';
import FormSelect from 'ui/FormSelect/formSelect.module';
import { Modal } from 'ui/modal/modal.module';

import { MessageWrapper } from 'components/messageWrapper/messageWrapper.module';
import { COUNTRY_CODES, CSS_COLORS } from 'consts/consts';
import { BUTTON_SIZE, BUTTON_TYPE, MESSAGE_TEXT_INPUT_TYPE, NODE_TYPES } from 'consts/enum';
import { LS } from 'consts/localStorage';
import { api } from 'helpers/api';
import {
	debug,
	deleteFromLs,
	generate6DigitUUID,
	isValidResponse,
	readFromLs,
	setCustomTagHTML,
	storeSkipInLS,
	writeToLs,
} from 'helpers/helpers';
import { ENGT_CHAT } from 'js/bot.model';

import { formData } from './form.model';
import { FormNode } from './form.template';

export default class Form {
	constructor(props) {
		this.initData(props);
		this.initWrapper();
		this.initComponents();
	}

	initData(propData = {}) {
		this.data = formData;
		const { otherChannelsData } = propData;
		const {
			attributes,
			enable_confirmation_section,
			confirmation_button,
			identification_type,
			skip_button,
			skip_login,
			subtitle,
			title,
		} = propData.message?.data;
		this.data.id = propData.id || propData.message_id || `formnode-message-${generate6DigitUUID()}`;
		this.setter({
			...propData,
			attributes,
			showConfirmationBox: enable_confirmation_section ?? true,
			confirmationButton: confirmation_button,
			identificationType: identification_type,
			skipButton: skip_button,
			skipLogin: skip_login,
			subtitle: subtitle && subtitle !== 'null' ? subtitle : '',
			title,
			otherChannelsData,
		});
		ENGT_CHAT.set('validateFormNode', this.validateFormNode.bind(this));
	}

	setter(propData) {
		this.data = {
			...this.data,
			...propData,
		};
	}

	initWrapper() {
		this.html = new MessageWrapper({
			...this.data,
			innerHTML: FormNode(this.data),
		});
	}

	disableInputFields() {
		const loginInputFields = this.html.querySelectorAll(`#formFields-${this.data.id} [data-field-role="input"]`);
		const loginSelectFields = this.html.querySelectorAll(`#engt-select-${this.data.id}`);

		loginInputFields.forEach((field) => {
			field.disabled = true;
			field.style.cursor = 'not-allowed';
		});
		loginSelectFields.length &&
			loginSelectFields.forEach((field) => {
				field.querySelector('.dropdown').innerHTML = '';
				field.addEventListener(
					'click',
					(e) => {
						e.stopPropagation();
					},
					true
				);
				field.style.cursor = 'not-allowed';
			});
	}

	dialogBoxClose() {
		this.modal.close('', true);
		document.querySelector(`#formDialogBox-${this.data.id}`)?.remove();
	}

	submitForm() {
		const loginInputFields = this.html.querySelectorAll(`#formFields-${this.data.id} [data-field-role="input"]`);
		const optionContainers = this.html.querySelectorAll('.engt-select-container');
		const optionFieldData = {};
		const loginInputData = {};
		const optionFieldTypes = {};
		for (let i = 0; i < optionContainers.length; i++) {
			const optionFieldValues = [];
			const dataObj = {};
			const optionFields = optionContainers[i].querySelectorAll('.engt-select-container input');

			for (let option = 0; option < optionFields.length; option++) {
				if (optionFields[option].checked) {
					optionFieldValues.push(optionFields[option].id);
				} else if (optionFields[option].type !== 'checkbox') {
					try {
						if (Array.from(optionFields[option].classList).indexOf('engt-select-override') > -1) {
							dataObj['isCountryCode'] = true;
							if (optionFields[option].getAttribute('data-attr-value') === null) {
								optionFieldValues.push(optionFields[option].value);
								continue;
							}
						}
					} catch (e) {
						console.error('DOM element doesnt have class');
					}
					optionFieldValues.push(optionFields[option].getAttribute('data-attr-value'));
				}
			}
			dataObj['value'] = optionFieldValues;

			const attrId = optionContainers[i].getAttribute('data-form-attrid');
			const attrType = optionContainers[i].getAttribute('data-form-type');

			optionFieldTypes[attrId] = attrType;
			if (optionFieldValues.length) {
				if (attrType === 'SINGLE_SELECT') {
					optionFieldData[attrId] = optionFieldValues?.[0] ?? '';
				} else {
					optionFieldData[attrId] = JSON.stringify(dataObj);
				}
			} else {
				optionFieldData[attrId] = '';
			}
		}
		for (let field = 0; field < loginInputFields.length; field++) {
			loginInputData[loginInputFields[field].id] =
				loginInputFields[field].value || loginInputFields[field].getAttribute('data-selected-date');
			loginInputFields[field].setAttribute('data-form-value', loginInputData[loginInputFields[field].id]);
		}
		if (Object.keys(loginInputData).length || Object.keys(optionFieldData).length) {
			Object.keys(optionFieldData).forEach((optionField) => {
				if (loginInputData[optionField] === null) {
					loginInputData[optionField] = '';
				} else {
					try {
						const optionValue =
							optionFieldTypes[optionField] === 'SINGLE_SELECT'
								? optionFieldData[optionField]
								: JSON.parse(optionFieldData[optionField]);

						if (optionValue.isCountryCode) {
							loginInputData[optionField] = `${optionValue.value[0]}-${loginInputData[optionField]}`;
						} else {
							loginInputData[optionField] = optionFieldData[optionField];
						}
					} catch (e) {
						loginInputData[optionField] = '';
					}
				}
			});
			writeToLs(
				`engt-submittedFormValues-${this.data.id}`,
				JSON.stringify({ values: loginInputData, attributes: this.data.attributes })
			);
			const message = { type: 'identity', params: loginInputData, action: {} };
			if (this.data.otherChannelsData?.bot && this.data.otherChannelsData?.platform) {
				const { bot, platform, user, nodeType } = this.data.otherChannelsData;
				let apiEndPoint = 'validateAndSubmitFormResponse';
				let payloadData = {
					request: JSON.stringify({ ...message, userId: user }),
					params: loginInputData,
					attributes: this.data.otherChannelsData.attributes,
					userId: user,
				};
				if (nodeType === NODE_TYPES.IDENTITY) {
					apiEndPoint = 'submitFormResponse';
					payloadData = {
						...message,
						userId: user,
					};
				}
				api('POST', `${window.location.origin}/${platform}/${apiEndPoint}/${bot}`, {
					payload: JSON.stringify(payloadData),
				})
					.then((resp) => {
						if (resp && nodeType === NODE_TYPES.IDENTITY) {
							const pageContainer = document.getElementById('engt-popup');
							pageContainer.innerHTML =
								'<div>Thanks for the input. Please close this web view to return to the chat.</div>';
							pageContainer.classList.add('engt-redirect-open');
							window.top.close();
						}
						const response = isValidResponse(resp);
						if (response) {
							if (response.status) {
								const pageContainer = document.getElementById('engt-popup');
								pageContainer.innerHTML =
									'<div style="color: red;">Thanks for the input. Please close this web view to return to the chat.</div>';
								pageContainer.classList.add('engt-redirect-open');
								window.top.close();
							} else {
								this.validateFormNode(response);
							}
						}
					})
					.catch((error) => {
						debug(error);
					});
			} else {
				ENGT_CHAT.get('sendMessage')('', { message, skipAppendToDom: true });
			}
		}
	}

	skipForm() {
		this.onSkip();
		this.disableInputFields();
		const formElements = document.getElementById(this.data.id).children[1];
		const formButtons = formElements.querySelectorAll('.engt-button');
		formButtons.forEach((element) => {
			formElements.removeChild(element);
		});
		setTimeout(() => {
			writeToLs(
				LS().TAB_MESSAGES,
				JSON.stringify({
					action: 'update_form_msg',
					selectedValue: 'skip',
					toggleBtnId: `engt-form-toggle-${this.data.id}`,
					id: this.data.id,
					tabId: ENGT_CHAT.get('socketId'),
				})
			);
		}, 300);
		storeSkipInLS();
		ENGT_CHAT.get('enableAllInput')();
	}

	showConfirmationBox(buttonData) {
		const content = document.createElement('div');
		content.classList.add('engt-form-dialog');
		const ref = this;
		const positiveButton = new Button({
			type: BUTTON_TYPE.PRIMARY,
			label: buttonData?.affirmativeText || ENGT_CHAT.get('GENERIC_STRINGS')?.YES || 'Yes',
			size: BUTTON_SIZE.MEDIUM,
			style: 'margin-top: 16px; margin-bottom: 8px;',
			onClick() {
				if (buttonData?.skipMessage) {
					ref.skipForm();
				} else {
					ref.submitForm();
				}
				ref.dialogBoxClose();
			},
			radius: 18,
		});
		const negativeButton = new Button({
			type: BUTTON_TYPE.SECONDARY,
			label: buttonData?.negativeText || ENGT_CHAT.get('GENERIC_STRINGS')?.NO || 'No',
			size: BUTTON_SIZE.MEDIUM,
			onClick() {
				ref.dialogBoxClose();
			},
			radius: 18,
		});

		const confirmationMessage =
			buttonData?.confirmationMessage ||
			ENGT_CHAT.get('GENERIC_STRINGS')?.FORM_SUBMIT ||
			'Are you sure you want to submit the form?';
		const skipMessage =
			buttonData?.skipMessage ||
			ENGT_CHAT.get('GENERIC_STRINGS')?.FORM_SKIP ||
			'Are you sure you want to skip the form?';

		content.innerHTML = `<p style="text-align: center;">${
			buttonData?.skipMessage ? skipMessage : confirmationMessage
		}</p>`;
		content.appendChild(positiveButton.render());
		content.appendChild(negativeButton.render());
		const chatContent = document.getElementById('engtChatContent');
		const computedStyles = getComputedStyle(chatContent);
		this.modal = new Modal({
			wrapperId: `formDialogBox-${this.data.id}`,
			contentId: `formDialogBoxContent-${this.data.id}`,
			content,
			wrapperCss: 'display: flex; justify-content: center; align-items:center;',
			contentCss: `max-width: ${
				chatContent.offsetWidth -
				parseFloat(computedStyles.paddingLeft) -
				parseFloat(computedStyles.paddingRight)
			}px;`,
			closeable: false,
		});
		document.querySelector('#engt-popup').appendChild(this.modal.render());
		this.modal.open();
	}

	initComponents() {
		const {
			attributes,
			skipLogin,
			confirmationButton,
			id,
			message_id,
			skipButton,
			message,
			identificationType,
			showConfirmationBox,
		} = this.data;
		const { data } = message;
		const { node_type } = data;
		const ref = this;
		const signFields = this.html.querySelector(`#formFields-${id}`);
		const skipPostBack = typeof skipButton === 'object' ? skipButton : JSON.parse(skipButton);
		const confirmationButtonData =
			typeof confirmationButton === 'object' ? confirmationButton : JSON.parse(confirmationButton);
		const isOldMsg = this.data.isOldMessage;
		function reorderCountryCodes(customCountryCodes) {
			const reorderedCountryCodes = {};
			customCountryCodes.forEach((code) => {
				if (COUNTRY_CODES[code]) {
					reorderedCountryCodes[code] = COUNTRY_CODES[code];
				}
			});
			Object.keys(COUNTRY_CODES).forEach((code) => {
				if (!customCountryCodes.includes(code)) {
					reorderedCountryCodes[code] = COUNTRY_CODES[code];
				}
			});

			return reorderedCountryCodes;
		}
		attributes?.map((el, id) => {
			if (el.attribute_type === 'object' || el.attribute_type === 'SINGLE_SELECT') {
				const data = { ...el };
				data.message_id = id;
				if (ref.data?.otherChannelsData) {
					const optionsObj = {};
					el?.options?.map((option) => {
						optionsObj[option.text] = option.postback;
					});
					data.options = JSON.stringify(optionsObj);
					data.isMultiselect = el?.is_multiselect_enabled || false;
				}
				if (el.attribute_type === 'SINGLE_SELECT') {
					try {
						const optionsObj = {};
						const options = typeof el?.options === 'string' ? JSON.parse(el?.options) : el?.options || [];
						options.map((option) => {
							optionsObj[option.option] = option.value;
						});
						data.options = JSON.stringify(optionsObj);
					} catch (e) {
						console.error(e);
					}
				}
				const formSelect = new FormSelect(data).render();
				const wrapper = document.createElement('div');
				wrapper.id = `engt-select-${this.data.id}`;
				wrapper.style.animationDelay = `${id * 0.2}s`;
				wrapper.classList.add('engt-form-field-wrapper', 'engt-form-field-z-index');
				wrapper.appendChild(formSelect);
				signFields.appendChild(wrapper);
			} else if (el.attribute_type === 'date' && el.show_date_picker?.toString() === 'true') {
				const wrapper = new Calendar({
					id: el.attribute_id,
					label: el.display_name,
				}).render();
				wrapper.style.animationDelay = `${id * 0.2}s`;
				wrapper.classList.add('engt-form-field-wrapper', 'engt-form-field-z-index');
				signFields.appendChild(wrapper);
			} else {
				if (el.attribute_type === 'date') {
					el.attribute_type = 'text';
				}
				if (
					el.attribute_type === 'mobile' &&
					el.show_country_code?.toString?.() === 'true' &&
					!ENGT_CHAT.get('config.isRudRespondBoxEnabled')
				) {
					el.inputWithSelect = true;
					const countryCodes = Array.isArray(ENGT_CHAT.get('config.customCountryCodes'))
						? reorderCountryCodes(ENGT_CHAT.get('config.customCountryCodes'))
						: COUNTRY_CODES;
					el.selectOptions = JSON.stringify(countryCodes);
					el.overrideOptionsClass = 'engt-override-dropdowns';
				}
				const formInput = new FormInput(el).render();
				const wrapper = document.createElement('div');
				wrapper.className = 'engt-text-sign-in-fields';
				wrapper.id = `engt-input-${id}`;
				wrapper.style.animationDelay = `${id * 0.2}s`;
				wrapper.classList.add('engt-form-field-wrapper', 'engt-form-field-z-index');
				wrapper.appendChild(formInput);
				signFields.appendChild(wrapper);
			}
		});

		const confirmButton = !isOldMsg
			? new Button({
					type: BUTTON_TYPE.PRIMARY,
					label:
						node_type === NODE_TYPES.IDENTITY
							? identificationType === 'API_BASED_AUTH'
								? 'Sign In'
								: ENGT_CHAT.get('GENERIC_STRINGS')?.CONTINUE || 'Continue'
							: confirmationButtonData?.confirmationText ||
							  ENGT_CHAT.get('GENERIC_STRINGS')?.SUBMIT ||
							  'Submit',
					id: `confirmBtn-${message_id}`,
					style: 'margin-top:20px',
					applyOnHover: true,
					dataInteractiveField: true,
					onClick() {
						if (node_type === NODE_TYPES.FORM_NODE && showConfirmationBox) {
							ref.showConfirmationBox(confirmationButtonData);
						} else {
							ref.submitForm();
						}
					},
					radius: 18,
			  }).render()
			: '';

		const skip = !isOldMsg
			? new Button({
					type: BUTTON_TYPE.MINI,
					label: skipPostBack?.skipText || ENGT_CHAT.get('GENERIC_STRINGS')?.SKIP || 'Skip',
					id: `skipBtn-${message_id}`,
					style: 'margin-top: 12px;',
					applyOnHover: true,
					dataInteractiveField: true,
					onClick() {
						if (node_type === NODE_TYPES.FORM_NODE) {
							ref.showConfirmationBox(skipPostBack);
						} else {
							ref.skipForm();
						}
					},
			  }).render()
			: '';
		setCustomTagHTML(this.html.querySelector('confirm-btn'), confirmButton);
		skipLogin && setCustomTagHTML(this.html.querySelector('skip-btn'), skip);

		isOldMsg && ref.disableInputFields();
	}

	validateFormNode = (data = {}) => {
		const { id, message_id, skipLogin } = this.data;
		const { login_success = false } = data;

		document.querySelectorAll(`#formFields-${id} [data-field-role="input"]`).forEach((el) => {
			el.style.borderColor = CSS_COLORS.ENGT_BORDER_COLOR;
		});
		document.querySelectorAll(`[data-form-id=engt-select-container-${id}]`).forEach((el) => {
			el.style.borderColor = CSS_COLORS.ENGT_BORDER_COLOR;
		});
		document.querySelectorAll(`#engt-input-${id} span[class=engt-error]`).forEach((el) => {
			el.innerHTML = '';
		});

		document.querySelectorAll(`#engt-select-error-${id} span`).forEach((el) => {
			el.innerHTML = '';
		});

		if (data?.attribute_error_message_mapping) {
			const allErrorFields = document.querySelectorAll(`#formFields-${id} .engt-error`);

			for (let i = 0; i < allErrorFields?.length; i++) {
				const errorElement = document.createElement('error-msg');
				errorElement.id = allErrorFields[i].id;
				allErrorFields[i].parentNode.replaceChild(errorElement, allErrorFields[i]);
			}

			Object.entries(data.attribute_error_message_mapping).map((el) => {
				document.getElementById(el[0]).style.borderColor = CSS_COLORS.ENGT_RED;
				document.querySelectorAll(`.engt-form-input-${el[0]}`).forEach((el) => {
					el.style.borderColor = CSS_COLORS.ENGT_RED;
				});
				const errMsg = document.createElement('span');
				const errorElement = document.querySelector(`#formFields-${id} #engt-error-${el[0]}`);
				errorElement.innerText = '';
				errMsg.innerHTML = el[1];
				errMsg.id = `engt-error-${el[0]}`;
				errMsg.className = 'engt-error';
				setCustomTagHTML(errorElement, errMsg);
			});
		} else {
			const msgNode = document.getElementById(`engt-form-${id}`);
			const formValues = JSON.parse(readFromLs(`engt-submittedFormValues-${this.data.id}`));
			const { attributes, values } = formValues || {};
			const attributeNameValues = {};
			attributes.forEach((attr) => {
				if (values[attr.attribute_id]) {
					if (attr.attribute_type === MESSAGE_TEXT_INPUT_TYPE.PASSWORD) {
						const passwordValue = values[attr.attribute_id];
						attributeNameValues[`${attr.display_name}`] = '*'.repeat(passwordValue.length);
					} else {
						attributeNameValues[`${attr.display_name}`] = values[attr.attribute_id];
					}
				} else {
					attributeNameValues[`${attr.display_name}`] = '-';
				}
			});

			const formFields = msgNode.querySelector('.engt-form-fields');
			formFields.innerHTML = '';
			Object.entries(attributeNameValues).map((attrField, index) => {
				const name = document.createElement('div');
				const val = document.createElement('div');
				let attrValue = attrField[1];
				name.innerText = attrField[0];
				try {
					const parsedValue = JSON.parse(attrValue);
					if (parsedValue?.value?.length) {
						attrValue = parsedValue.value.join(', ');
					}
				} catch (e) {
					console.error('couldnt parse object value', e);
				}
				val.innerHTML = `<strong>${attrValue}</strong>`;
				if (index > 0) {
					name.style.marginTop = '10px';
				}
				formFields.appendChild(name);
				formFields.appendChild(val);
			});
			document.getElementById(`confirmBtn-${message_id}`)?.remove();
			skipLogin && document.getElementById(`skipBtn-${message_id}`)?.remove();
			deleteFromLs(`engt-submittedFormValues-${this.data.id}`);
			ENGT_CHAT.get('enableAllInput')();
		}
		if (login_success && document.getElementById(`engt-form-${id}`).scrollHeight > 204) {
			document
				.getElementById(`engt-form-${id}`)
				.classList.add('engt-msg-formnode-shrink', 'engt-msg-formnode-shrinks');
			this.appendToggle();
		}
		if (login_success) {
			setTimeout(() => {
				writeToLs(
					LS().TAB_MESSAGES,
					JSON.stringify({
						action: 'update_form_msg',
						selectedValue: document.getElementById(`engt-form-${id}`).innerHTML,
						toggleBtnId: `engt-form-toggle-${id}`,
						id: `engt-form-${id}`,
						tabId: ENGT_CHAT.get('socketId'),
					})
				);
			}, 600);
		}
	};

	generateToggle() {
		const ref = this;
		const { id } = ref.data;
		const showMoreDiv = document.createElement('div');
		const arrow = document.createElement('img');

		showMoreDiv.className = 'engt-toggle-size';
		showMoreDiv.id = `engt-form-toggle-${id}`;
		showMoreDiv.setAttribute('data-open', false);
		showMoreDiv.innerHTML = '<span>Show more</span>';
		arrow.className = 'engt-show-more-icon';
		arrow.src = DownArrow;
		showMoreDiv.appendChild(arrow);

		showMoreDiv.addEventListener('click', function () {
			if (this.getAttribute('data-open') === 'false') {
				ref.showMore.call(this, ref.html.querySelector(`#engt-form-${id}`));
			} else {
				ref.showLess.call(this, ref.html.querySelector(`#engt-form-${id}`));
			}
		});

		return showMoreDiv;
	}

	appendToggle() {
		const { id } = this.data;
		this.setter({
			toggleBtn: this.generateToggle(),
		});
		this.html.querySelector(`#engt-form-${id}`).appendChild(this.data.toggleBtn);
	}

	showMore(parent) {
		parent.classList.remove('engt-msg-formnode-shrink');
		this.setAttribute('data-open', true);
		this.querySelector('span').innerText = 'Show less';
		this.querySelector('.engt-show-more-icon').src = UpArrow;
	}

	showLess(parent) {
		parent.classList.add('engt-msg-formnode-shrink');
		this.setAttribute('data-open', false);
		this.querySelector('span').innerText = 'Show more';
		this.querySelector('.engt-show-more-icon').src = DownArrow;
	}

	onSkip() {
		ENGT_CHAT.get('sendMessage')('skip', {
			message: {
				action: { skip_login: true },
				type: 'identity',
			},
			skipAppendToDom: true,
			tab_msg_action: 'skip',
		});
	}

	render() {
		return this.html;
	}
}
